/**
 * useTemplateFilters — search filtering, sorting, and the Standard/Yours
 * grouping for the Blog Templates library.
 *
 * Extracted from BlogTemplatesPage (Phase 1 decomposition). Category
 * filtering was REMOVED per the 2026-07-23 Carl ruling ("CATEGORY FILTERS:
 * DROP NOW" — resolves adjudication R9's transitional keep; see
 * docs/research/template-builder/SPEC-category-filter-removal.md). Filtering
 * is now search-only; search covers narrowing. Sort order is unchanged:
 * popular → new → alphabetical, applied within each group.
 *
 * Grouping (adjudicated R2 / Fable §4.1): this hook exposes grouped lists —
 * `yoursTemplates` (filtered, !is_system) and `standardTemplates` (filtered,
 * is_system). The All/System/My tabs and tagged-set branches are gone (R2).
 */

import { useMemo, useState } from 'react';
import type { BlogTemplateSummary } from '@/services/blogTemplatesService';

export interface UseTemplateFiltersResult {
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  /** Search-filtered + sorted group: tenant-owned / non-system templates (R2). */
  yoursTemplates: BlogTemplateSummary[];
  /** Search-filtered + sorted group: platform-provided system templates (R2). */
  standardTemplates: BlogTemplateSummary[];
  clearFilters: () => void;
}

/** Popular first, then New, then alphabetical — unchanged from the page. */
function sortTemplates(list: BlogTemplateSummary[]): BlogTemplateSummary[] {
  return [...list].sort((a, b) => {
    if (a.is_popular && !b.is_popular) return -1;
    if (!a.is_popular && b.is_popular) return 1;
    if (a.is_new && !b.is_new) return -1;
    if (!a.is_new && b.is_new) return 1;
    return a.name.localeCompare(b.name);
  });
}

export function useTemplateFilters(
  templates: BlogTemplateSummary[],
): UseTemplateFiltersResult {
  const [searchQuery, setSearchQuery] = useState('');

  // Search-only filtering (category filtering removed per the 2026-07-23
  // Carl ruling; the viewFilter/'custom' tagged branch is gone per R2)
  const filteredTemplates = useMemo(() => {
    return templates.filter((template) => {
      return (
        searchQuery === '' ||
        template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (template.description?.toLowerCase().includes(searchQuery.toLowerCase()) ?? false) ||
        template.features.some((f) =>
          f.toLowerCase().includes(searchQuery.toLowerCase())
        )
      );
    });
  }, [templates, searchQuery]);

  // Sort: Popular first, then New, then alphabetical
  const sortedTemplates = useMemo(() => sortTemplates(filteredTemplates), [filteredTemplates]);

  // Standard / Yours grouping (R2) — same sort within each group
  const yoursTemplates = useMemo(
    () => sortedTemplates.filter((t) => !t.is_system),
    [sortedTemplates]
  );
  const standardTemplates = useMemo(
    () => sortedTemplates.filter((t) => t.is_system),
    [sortedTemplates]
  );

  const clearFilters = () => {
    setSearchQuery('');
  };

  return {
    searchQuery,
    setSearchQuery,
    yoursTemplates,
    standardTemplates,
    clearFilters,
  };
}
